import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Alert, AlertTitle } from '@mui/material'
import { useSelector } from 'react-redux'
import { SubmissionError, SubmissionErrorCode, User } from '@global/types'
import { RootState } from '@redux/store'
import { ErrorCodeTextCopyable } from './components'

const SpanWithParens = styled.span`
  &:before {
    content: '(';
  }

  &:after {
    content: ')';
  }
`

interface Props {
  error: SubmissionError
}

export const ErrorAlert = ({ error }: Props) => {
  const { type_code } = error
  const user: User | null = useSelector((state: RootState) => state.Auth.user)
  const isSuperAdmin = Boolean(user?.super_admin)
  const errorMessage = getErrorMessage(type_code)
  const errorCodeText = `Error Code ${type_code}`

  return (
    <Alert
      severity="error"
      sx={{
        backgroundColor: '#FEECEB',
        color: '#5f2120',
        maxWidth: 870,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <AlertTitle>{t`Processing error`}</AlertTitle>
      {errorMessage}
      &nbsp;
      <SpanWithParens>
        {isSuperAdmin ? (
          <ErrorCodeTextCopyable errorCodeText={errorCodeText} error={error} />
        ) : (
          errorCodeText
        )}
      </SpanWithParens>
      <span>.</span>
    </Alert>
  )
}

function getErrorMessage(errorCode: SubmissionErrorCode) {
  switch (errorCode) {
    case 5105: {
      return <span>{t`Data processing timed out. Please try again later`}</span>
    }
    case 4500: {
      return (
        <span>
          {t`This template contains deleted fields. Please remove the deleted fields from the template and try again`}
        </span>
      )
    }
    case 4600: {
      return (
        <span>
          {t`This submission contains cells that have a length greater than 4096 characters. Please shorten those fields and try again.`}
        </span>
      )
    }
    default: {
      return (
        <span>
          <Trans>
            We encountered an error processing your data. Please contact our support team at{' '}
            <a href="mailto:support@claravine.com" target="_blank" rel="noreferrer">
              support@claravine.com
            </a>{' '}
            or reach out to an account admin for assistance
          </Trans>
        </span>
      )
    }
  }
}
